<script setup>
import { ref, computed, onMounted } from 'vue'
import { useEmail } from '@core/composables/useEmail.js'
import {
  getCommits,
  getCommitsStats,
  searchCommits,
  markCommitEmailSent,
  subscribeCommits,
  getCommitEmailRecipients,
  saveCommitEmailRecipients,
} from '@core/services/commits.js'
import { generateCommitEmailHtml } from '@core/utils/emailTemplates.js'
import { useAuthStore } from '@app/store/auth.js'
import { formatDateTime } from '@core/utils/formatting.js'
import { usePagination } from '@core/composables/usePagination.js'
import { useConfirmDialog } from '@core/composables/useConfirmDialog.js'

const authStore = useAuthStore()
const { confirm } = useConfirmDialog()

// ============================================
// State
// ============================================
const commits = ref([])
const isLoading = ref(true)
const error = ref(null)
const stats = ref({
  totalCommits: 0,
  commitsThisMonth: 0,
  totalLinesChanged: 0,
  avgLinesPerCommit: 0,
})

// Filters
const searchQuery = ref('')
const dateFrom = ref('')
const dateTo = ref('')
const authorFilter = ref('')
const branchFilter = ref('')
const emailStatusFilter = ref('') // '', 'sent', 'not-sent'
const filtersCollapsed = ref(true)

// Expand/collapse state (Set of commit hashes)
const expandedRows = ref(new Set())

// Email state
const { sendEmail, sending: emailSending } = useEmail()
const sendingEmail = ref(new Set()) // Set of commit IDs currently sending

// Email config modal state
const showEmailModal = ref(false)
const emailRecipients = ref([])
const disabledEmails = ref(new Set())
const newEmailInput = ref('')
const savingEmails = ref(false)

// ============================================
// Fetch Data
// ============================================
async function fetchCommits() {
  isLoading.value = true
  error.value = null

  try {
    const [commitsData, statsData] = await Promise.all([
      getCommits(200), // Fetch more for client-side filtering
      getCommitsStats(),
    ])

    commits.value = commitsData
    stats.value = statsData
  } catch (err) {
    console.error('Failed to fetch commits:', err)
    error.value = 'שגיאה בטעינת קומיטים'
  } finally {
    isLoading.value = false
  }
}

onMounted(() => {
  fetchCommits()
  loadEmailRecipients()
  if (window.innerWidth < 768) {
    filtersCollapsed.value = true
  }
})

// ============================================
// Filtering & Search
// ============================================
const filteredCommits = computed(() => {
  let result = [...commits.value]

  // Text search
  if (searchQuery.value.trim()) {
    const search = searchQuery.value.toLowerCase().trim()
    result = result.filter((c) => {
      return (
        c.subject?.toLowerCase().includes(search) ||
        c.body?.toLowerCase().includes(search) ||
        c.author?.toLowerCase().includes(search) ||
        c.shortHash?.toLowerCase().includes(search)
      )
    })
  }

  // Date range filter
  if (dateFrom.value) {
    const from = new Date(dateFrom.value)
    result = result.filter((c) => {
      const commitDate = c.date?.toDate ? c.date.toDate() : new Date(c.dateISO)
      return commitDate >= from
    })
  }

  if (dateTo.value) {
    const to = new Date(dateTo.value)
    to.setHours(23, 59, 59, 999)
    result = result.filter((c) => {
      const commitDate = c.date?.toDate ? c.date.toDate() : new Date(c.dateISO)
      return commitDate <= to
    })
  }

  // Author filter
  if (authorFilter.value) {
    result = result.filter((c) => c.author === authorFilter.value)
  }

  // Branch filter
  if (branchFilter.value) {
    result = result.filter((c) => c.branch === branchFilter.value)
  }

  // Email status filter
  if (emailStatusFilter.value === 'sent') {
    result = result.filter((c) => c.emailSent)
  } else if (emailStatusFilter.value === 'not-sent') {
    result = result.filter((c) => !c.emailSent)
  }

  return result
})

// Get unique authors
const uniqueAuthors = computed(() => {
  const authors = new Set(commits.value.map((c) => c.author).filter(Boolean))
  return Array.from(authors).sort()
})

// Get unique branches
const uniqueBranches = computed(() => {
  const branches = new Set(commits.value.map((c) => c.branch).filter(Boolean))
  return Array.from(branches).sort()
})

// Active filters count
const activeFiltersCount = computed(() => {
  let count = 0
  if (searchQuery.value) count++
  if (dateFrom.value) count++
  if (dateTo.value) count++
  if (authorFilter.value) count++
  if (branchFilter.value) count++
  if (emailStatusFilter.value) count++
  return count
})

function clearFilters() {
  searchQuery.value = ''
  dateFrom.value = ''
  dateTo.value = ''
  authorFilter.value = ''
  branchFilter.value = ''
  emailStatusFilter.value = ''
  resetPagination()
}

// ============================================
// Pagination
// ============================================
const {
  currentPage,
  pageSize,
  totalPages,
  paginatedItems: paginatedCommits,
  pageSizeOptions,
  resetPagination,
  prevPage,
  nextPage,
} = usePagination(filteredCommits, {
  defaultPageSize: 25,
  pageSizeOptions: [25, 50, 100],
})

// ============================================
// Row Expansion
// ============================================
function toggleRow(commitHash) {
  if (expandedRows.value.has(commitHash)) {
    expandedRows.value.delete(commitHash)
  } else {
    expandedRows.value.add(commitHash)
  }
}

function isRowExpanded(commitHash) {
  return expandedRows.value.has(commitHash)
}

// ============================================
// Email Sending
// ============================================
async function handleEmailClick(commit) {
  if (sendingEmail.value.has(commit.hash)) return

  // Filter out disabled emails
  const activeRecipients = emailRecipients.value.filter((email) => !disabledEmails.value.has(email))

  if (activeRecipients.length === 0) {
    alert('⚠️ אין כתובות מייל פעילות. לחץ על "הגדר מיילים" כדי להפעיל נמענים.')
    return
  }
  if (commit.emailSent) {
    const ok = await confirm({
      title: 'שליחת אימייל חוזרת',
      message: 'אימייל כבר נשלח. לשלוח שוב?',
      confirmText: 'שלח שוב',
      variant: 'warning',
    })
    if (!ok) return
  }

  sendingEmail.value.add(commit.hash)

  try {
    // Generate email HTML with sender's profile info
    const html = generateCommitEmailHtml(commit, {
      photoURL: authStore.userPhotoURL,
      displayName: authStore.userDisplayName,
    })

    // Send email to active recipients only
    const emailDocId = await sendEmail({
      to: activeRecipients,
      subject: `עדכון טכני: ${commit.subject}`,
      html,
      from: 'GoFood פורטל סוכנים',
    })

    // Mark as sent in Firestore
    await markCommitEmailSent(commit.hash, emailDocId)

    // Update local state
    const index = commits.value.findIndex((c) => c.hash === commit.hash)
    if (index !== -1) {
      commits.value[index] = {
        ...commits.value[index],
        emailSent: true,
        emailDocId,
      }
    }

    alert('✅ אימייל נשלח בהצלחה!')
  } catch (err) {
    console.error('Failed to send email:', err)
    alert('❌ שגיאה בשליחת אימייל')
  } finally {
    sendingEmail.value.delete(commit.hash)
  }
}

function isEmailSending(commitHash) {
  return sendingEmail.value.has(commitHash)
}

// ============================================
// Email Config Modal
// ============================================
async function loadEmailRecipients() {
  try {
    const result = await getCommitEmailRecipients()
    emailRecipients.value = result.emails
    disabledEmails.value = new Set(result.disabledEmails)
  } catch (err) {
    console.error('Failed to load email recipients:', err)
  }
}

function openEmailModal() {
  showEmailModal.value = true
  newEmailInput.value = ''
}

async function closeEmailModal() {
  showEmailModal.value = false
  newEmailInput.value = ''
  // Reload from Firestore to discard unsaved local changes
  await loadEmailRecipients()
}

function addEmail() {
  const email = newEmailInput.value.trim()
  if (!email) return

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  if (!emailRegex.test(email)) {
    alert('⚠️ כתובת מייל לא תקינה')
    return
  }

  if (emailRecipients.value.includes(email)) {
    alert('⚠️ כתובת זו כבר קיימת ברשימה')
    return
  }

  emailRecipients.value.push(email)
  newEmailInput.value = ''
}

function removeEmail(index) {
  const email = emailRecipients.value[index]
  disabledEmails.value.delete(email)
  emailRecipients.value.splice(index, 1)
}

function toggleEmailEnabled(email) {
  if (disabledEmails.value.has(email)) {
    disabledEmails.value.delete(email)
  } else {
    disabledEmails.value.add(email)
  }
  // Trigger reactivity
  disabledEmails.value = new Set(disabledEmails.value)
}

function isEmailDisabled(email) {
  return disabledEmails.value.has(email)
}

async function saveEmails() {
  savingEmails.value = true
  try {
    await saveCommitEmailRecipients(emailRecipients.value, [...disabledEmails.value])
    closeEmailModal()
  } catch (err) {
    console.error('Failed to save email recipients:', err)
    alert('❌ שגיאה בשמירת כתובות מייל')
  } finally {
    savingEmails.value = false
  }
}

// ============================================
// Formatting Utilities
// ============================================
function copyToClipboard(text) {
  navigator.clipboard.writeText(text).then(
    () => alert('✅ הועתק ללוח'),
    () => alert('❌ שגיאה בהעתקה')
  )
}
</script>

<template>
  <div class="commits-view">
    <!-- Header -->
    <div class="header">
      <div class="header-title">
        <div class="title-badge">
          <svg viewBox="0 0 24 24" width="24" height="24">
            <circle cx="12" cy="12" r="3" fill="currentColor" />
            <circle cx="12" cy="5" r="1.5" fill="currentColor" />
            <circle cx="19" cy="12" r="1.5" fill="currentColor" />
            <circle cx="12" cy="19" r="1.5" fill="currentColor" />
            <circle cx="5" cy="12" r="1.5" fill="currentColor" />
            <path
              d="M12 9v6M9 12h6"
              stroke="currentColor"
              stroke-width="1.5"
              fill="none"
              stroke-linecap="round"
            />
          </svg>
        </div>
        <h1>קומיטים</h1>
      </div>
      <div class="header-actions">
        <button class="email-config-btn" @click="openEmailModal">
          <svg viewBox="0 0 24 24" width="18" height="18">
            <path
              d="M3 8l7.89 5.26a2 2 0 0 0 2.22 0L21 8M5 19h14a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2z"
              stroke="currentColor"
              stroke-width="2"
              fill="none"
            />
          </svg>
          <span>הגדר מיילים</span>
          <span v-if="emailRecipients.length" class="email-count-badge"
            >{{ emailRecipients.filter((e) => !disabledEmails.has(e)).length }}/{{
              emailRecipients.length
            }}</span
          >
        </button>

        <button class="refresh-btn" @click="fetchCommits" :disabled="isLoading">
          <svg viewBox="0 0 24 24" width="18" height="18">
            <path
              d="M4 12a8 8 0 0 1 8-8V2l4 4-4 4V8a6 6 0 0 0 0 12c1.66 0 3.17-.67 4.24-1.76l1.42 1.42A7.95 7.95 0 0 1 12 22a8 8 0 0 1-8-8z"
              fill="currentColor"
            />
          </svg>
          <span>רענן</span>
        </button>
      </div>
    </div>

    <!-- Stats Cards -->
    <div class="stats-cards">
      <div class="stat-card">
        <div class="stat-label">סה״כ קומיטים</div>
        <div class="stat-value">{{ stats.totalCommits }}</div>
      </div>
      <div class="stat-card">
        <div class="stat-label">החודש</div>
        <div class="stat-value">{{ stats.commitsThisMonth }}</div>
      </div>
      <div class="stat-card">
        <div class="stat-label">שורות שונו</div>
        <div class="stat-value">{{ stats.totalLinesChanged.toLocaleString() }}</div>
      </div>
      <div class="stat-card">
        <div class="stat-label">ממוצע לקומיט</div>
        <div class="stat-value">{{ stats.avgLinesPerCommit }}</div>
      </div>
    </div>

    <!-- Filters Bar -->
    <div class="filters-bar" :class="{ collapsed: filtersCollapsed }">
      <div class="filters-header" @click="filtersCollapsed = !filtersCollapsed">
        <div class="filters-header-title">
          <svg viewBox="0 0 24 24" width="16" height="16">
            <path
              d="M4 6h16M4 12h12M4 18h8"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              fill="none"
            />
          </svg>
          <span>סינונים</span>
          <span v-if="activeFiltersCount > 0" class="filters-count-badge">{{
            activeFiltersCount
          }}</span>
        </div>
        <svg
          class="collapse-icon"
          :class="{ rotated: filtersCollapsed }"
          viewBox="0 0 24 24"
          width="16"
          height="16"
        >
          <path d="M6 9l6 6 6-6" stroke="currentColor" stroke-width="2" fill="none" />
        </svg>
      </div>

      <div v-if="!filtersCollapsed" class="filters-content">
        <div class="filter-group">
          <label>חיפוש</label>
          <input
            v-model="searchQuery"
            type="text"
            placeholder="חפש לפי hash, נושא, תוכן, יוצר..."
            @input="resetPagination"
          />
        </div>

        <div class="filter-row">
          <div class="filter-group">
            <label>מתאריך</label>
            <input v-model="dateFrom" type="date" @change="resetPagination" />
          </div>

          <div class="filter-group">
            <label>עד תאריך</label>
            <input v-model="dateTo" type="date" @change="resetPagination" />
          </div>
        </div>

        <div class="filter-row">
          <div class="filter-group">
            <label>יוצר</label>
            <select v-model="authorFilter" @change="resetPagination">
              <option value="">כולם</option>
              <option v-for="author in uniqueAuthors" :key="author" :value="author">
                {{ author }}
              </option>
            </select>
          </div>

          <div class="filter-group">
            <label>ענף</label>
            <select v-model="branchFilter" @change="resetPagination">
              <option value="">כולם</option>
              <option v-for="branch in uniqueBranches" :key="branch" :value="branch">
                {{ branch }}
              </option>
            </select>
          </div>

          <div class="filter-group">
            <label>סטטוס אימייל</label>
            <select v-model="emailStatusFilter" @change="resetPagination">
              <option value="">הכל</option>
              <option value="sent">נשלח</option>
              <option value="not-sent">לא נשלח</option>
            </select>
          </div>
        </div>

        <div v-if="activeFiltersCount > 0" class="filter-actions">
          <button class="clear-filters-btn" @click="clearFilters">נקה סינונים</button>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="isLoading" class="loading-state">
      <div class="spinner"></div>
      <p>טוען קומיטים...</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="error-state">
      <svg viewBox="0 0 24 24" width="48" height="48">
        <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" fill="none" />
        <path d="M12 8v4M12 16h.01" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
      </svg>
      <p>{{ error }}</p>
      <button @click="fetchCommits">נסה שוב</button>
    </div>

    <!-- Empty State -->
    <div v-else-if="paginatedCommits.length === 0" class="empty-state">
      <svg viewBox="0 0 24 24" width="48" height="48">
        <path
          d="M12 2L2 7v10c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V7l-10-5z"
          stroke="currentColor"
          stroke-width="2"
          fill="none"
        />
      </svg>
      <p v-if="activeFiltersCount > 0">לא נמצאו קומיטים התואמים את הסינון</p>
      <p v-else>טרם נשמרו קומיטים במערכת</p>
    </div>

    <!-- Commits Table (Desktop) -->
    <div v-else class="commits-table-container desktop-only">
      <table class="commits-table">
        <thead>
          <tr>
            <th style="width: 40px"></th>
            <th style="width: 100px">Hash</th>
            <th style="width: 140px">תאריך</th>
            <th style="width: 120px">יוצר</th>
            <th>נושא</th>
            <th style="width: 100px">סטטיסטיקה</th>
            <th style="width: 80px">אימייל</th>
          </tr>
        </thead>
        <tbody>
          <template v-for="commit in paginatedCommits" :key="commit.hash">
            <!-- Main Row -->
            <tr class="commit-row" :class="{ expanded: isRowExpanded(commit.hash) }">
              <td>
                <button class="expand-btn" @click="toggleRow(commit.hash)">
                  <svg
                    viewBox="0 0 24 24"
                    width="16"
                    height="16"
                    :class="{ rotated: isRowExpanded(commit.hash) }"
                  >
                    <path
                      d="M9 6l6 6-6 6"
                      stroke="currentColor"
                      stroke-width="2"
                      fill="none"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </button>
              </td>
              <td>
                <code class="commit-hash" @click="copyToClipboard(commit.hash)" title="העתק">
                  {{ commit.shortHash }}
                </code>
              </td>
              <td class="date-cell">
                {{ formatDateTime(commit.date || commit.dateISO, { short: true }) }}
              </td>
              <td class="author-cell">{{ commit.author }}</td>
              <td class="subject-cell">{{ commit.subject }}</td>
              <td class="stats-cell">
                <div class="stats-mini">
                  <span class="stats-added" :title="`${commit.stats?.linesAdded || 0} שורות נוספו`">
                    +{{ commit.stats?.linesAdded || 0 }}
                  </span>
                  <span
                    class="stats-deleted"
                    :title="`${commit.stats?.linesDeleted || 0} שורות נמחקו`"
                  >
                    -{{ commit.stats?.linesDeleted || 0 }}
                  </span>
                  <span class="stats-files" :title="`${commit.stats?.filesChanged || 0} קבצים`">
                    {{ commit.stats?.filesChanged || 0 }}📄
                  </span>
                </div>
              </td>
              <td class="email-cell">
                <button
                  class="email-btn"
                  :class="{ sent: commit.emailSent }"
                  @click="handleEmailClick(commit)"
                  :disabled="isEmailSending(commit.hash)"
                  :title="commit.emailSent ? 'אימייל נשלח — לחץ לשליחה חוזרת' : 'שלח אימייל'"
                >
                  <span v-if="isEmailSending(commit.hash)" class="mini-spinner"></span>
                  <template v-else-if="commit.emailSent">✓</template>
                  <svg v-else viewBox="0 0 24 24" width="16" height="16">
                    <path
                      d="M3 8l7.89 5.26a2 2 0 0 0 2.22 0L21 8M5 19h14a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2z"
                      stroke="currentColor"
                      stroke-width="2"
                      fill="none"
                    />
                  </svg>
                </button>
              </td>
            </tr>

            <!-- Expanded Row Details -->
            <tr v-if="isRowExpanded(commit.hash)" class="expanded-details">
              <td colspan="7">
                <div class="details-content">
                  <!-- Full Message -->
                  <div v-if="commit.body" class="detail-section">
                    <h4>תיאור מלא:</h4>
                    <pre class="commit-body">{{ commit.body }}</pre>
                  </div>

                  <!-- File Statistics -->
                  <div v-if="commit.stats?.files?.length" class="detail-section">
                    <h4>קבצים שונו ({{ commit.stats.filesChanged }}):</h4>
                    <table class="files-table">
                      <thead>
                        <tr>
                          <th>קובץ</th>
                          <th style="width: 80px">נוספו</th>
                          <th style="width: 80px">נמחקו</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(file, idx) in commit.stats.files" :key="idx">
                          <td class="filename-cell">{{ file.filename }}</td>
                          <td class="added-cell">+{{ file.added }}</td>
                          <td class="deleted-cell">-{{ file.deleted }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <!-- Metadata -->
                  <div class="detail-section metadata">
                    <div class="metadata-item">
                      <strong>ענף:</strong> <code>{{ commit.branch || 'master' }}</code>
                    </div>
                    <div class="metadata-item">
                      <strong>Email:</strong> <code>{{ commit.email }}</code>
                    </div>
                    <div class="metadata-item">
                      <strong>Hash מלא:</strong>
                      <code @click="copyToClipboard(commit.hash)" style="cursor: pointer">{{
                        commit.hash
                      }}</code>
                    </div>
                  </div>
                </div>
              </td>
            </tr>
          </template>
        </tbody>
      </table>

      <!-- Pagination -->
      <div class="pagination">
        <div class="pagination-info">
          מציג {{ (currentPage - 1) * pageSize + 1 }}-{{
            Math.min(currentPage * pageSize, filteredCommits.length)
          }}
          מתוך {{ filteredCommits.length }}
        </div>

        <div class="pagination-controls">
          <button @click="prevPage" :disabled="currentPage === 1">הקודם</button>
          <span class="page-indicator">עמוד {{ currentPage }} מתוך {{ totalPages }}</span>
          <button @click="nextPage" :disabled="currentPage >= totalPages">הבא</button>
        </div>

        <div class="page-size-selector">
          <label>פריטים בעמוד:</label>
          <select v-model.number="pageSize" @change="resetPagination">
            <option v-for="size in pageSizeOptions" :key="size" :value="size">{{ size }}</option>
          </select>
        </div>
      </div>
    </div>

    <!-- Commits Cards (Mobile) -->
    <div
      v-if="!isLoading && !error && paginatedCommits.length > 0"
      class="commits-cards-container mobile-only"
    >
      <div
        v-for="commit in paginatedCommits"
        :key="'card-' + commit.hash"
        class="commit-card"
        :class="{ expanded: isRowExpanded(commit.hash) }"
      >
        <div class="card-header" @click="toggleRow(commit.hash)">
          <div class="card-top-row">
            <code class="commit-hash" @click.stop="copyToClipboard(commit.hash)">{{
              commit.shortHash
            }}</code>
            <span class="card-date">{{
              formatDateTime(commit.date || commit.dateISO, { short: true })
            }}</span>
          </div>
          <div class="card-subject">{{ commit.subject }}</div>
          <div class="card-bottom-row">
            <div class="stats-mini">
              <span class="stats-added">+{{ commit.stats?.linesAdded || 0 }}</span>
              <span class="stats-deleted">-{{ commit.stats?.linesDeleted || 0 }}</span>
              <span class="stats-files">{{ commit.stats?.filesChanged || 0 }}📄</span>
            </div>
            <button
              class="email-btn"
              :class="{ sent: commit.emailSent }"
              @click.stop="handleEmailClick(commit)"
              :disabled="isEmailSending(commit.hash)"
            >
              <span v-if="isEmailSending(commit.hash)" class="mini-spinner"></span>
              <template v-else-if="commit.emailSent">✓</template>
              <svg v-else viewBox="0 0 24 24" width="16" height="16">
                <path
                  d="M3 8l7.89 5.26a2 2 0 0 0 2.22 0L21 8M5 19h14a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2z"
                  stroke="currentColor"
                  stroke-width="2"
                  fill="none"
                />
              </svg>
            </button>
          </div>
        </div>

        <!-- Expanded Card Details -->
        <div v-if="isRowExpanded(commit.hash)" class="card-details">
          <div class="card-detail-row">
            <span class="detail-label">יוצר:</span>
            <span class="detail-value">{{ commit.author }}</span>
          </div>
          <div class="card-detail-row">
            <span class="detail-label">ענף:</span>
            <code class="detail-value">{{ commit.branch || 'master' }}</code>
          </div>
          <div class="card-detail-row">
            <span class="detail-label">Email:</span>
            <code class="detail-value" style="direction: ltr">{{ commit.email }}</code>
          </div>
          <div class="card-detail-row">
            <span class="detail-label">Hash:</span>
            <code
              class="detail-value hash-copy"
              @click="copyToClipboard(commit.hash)"
              style="direction: ltr"
              >{{ commit.hash }}</code
            >
          </div>

          <div v-if="commit.body" class="card-detail-section">
            <h4>תיאור מלא:</h4>
            <pre class="commit-body">{{ commit.body }}</pre>
          </div>

          <div v-if="commit.stats?.files?.length" class="card-detail-section">
            <h4>קבצים שונו ({{ commit.stats.filesChanged }}):</h4>
            <div class="card-files-list">
              <div v-for="(file, idx) in commit.stats.files" :key="idx" class="card-file-item">
                <span class="card-filename">{{ file.filename }}</span>
                <div class="card-file-stats">
                  <span class="stats-added">+{{ file.added }}</span>
                  <span class="stats-deleted">-{{ file.deleted }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Mobile Pagination -->
      <div class="pagination mobile-pagination">
        <div class="pagination-info">
          מציג {{ (currentPage - 1) * pageSize + 1 }}-{{
            Math.min(currentPage * pageSize, filteredCommits.length)
          }}
          מתוך {{ filteredCommits.length }}
        </div>
        <div class="pagination-controls">
          <button @click="prevPage" :disabled="currentPage === 1">הקודם</button>
          <span class="page-indicator">{{ currentPage }} / {{ totalPages }}</span>
          <button @click="nextPage" :disabled="currentPage >= totalPages">הבא</button>
        </div>
      </div>
    </div>
  </div>

  <!-- Email Config Modal -->
  <Teleport to="body">
    <div v-if="showEmailModal" class="modal-overlay" @click.self="closeEmailModal">
      <div class="modal-card">
        <div class="modal-header">
          <h3>הגדרת מיילים לקומיטים</h3>
          <button class="modal-close-btn" @click="closeEmailModal">&times;</button>
        </div>

        <div class="modal-body">
          <div v-if="emailRecipients.length === 0" class="modal-empty">לא הוגדרו כתובות מייל</div>
          <ul v-else class="email-list">
            <li
              v-for="(email, index) in emailRecipients"
              :key="index"
              class="email-list-item"
              :class="{ 'email-disabled': isEmailDisabled(email) }"
            >
              <button
                class="email-toggle-btn"
                :class="{ active: !isEmailDisabled(email) }"
                :title="isEmailDisabled(email) ? 'הפעל' : 'השבת'"
                @click="toggleEmailEnabled(email)"
              >
                <span class="toggle-track">
                  <span class="toggle-thumb"></span>
                </span>
              </button>
              <span class="email-address">{{ email }}</span>
              <button class="email-remove-btn" @click="removeEmail(index)" title="הסר">
                &times;
              </button>
            </li>
          </ul>

          <div class="email-add-row">
            <input
              v-model="newEmailInput"
              type="email"
              placeholder="הזן כתובת מייל..."
              @keyup.enter="addEmail"
              class="email-input"
            />
            <button class="email-add-btn" @click="addEmail">הוסף</button>
          </div>
        </div>

        <div class="modal-footer">
          <button class="modal-save-btn" @click="saveEmails" :disabled="savingEmails">
            {{ savingEmails ? 'שומר...' : 'שמור' }}
          </button>
          <button class="modal-cancel-btn" @click="closeEmailModal">ביטול</button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.commits-view {
  padding: 24px;
  max-width: 1400px;
  margin: 0 auto;
}

/* Header */
.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.header-title {
  display: flex;
  align-items: center;
  gap: 12px;
}

.title-badge {
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
}

.header h1 {
  margin: 0;
  font-size: 28px;
  font-weight: 600;
  color: #1e293b;
}

.header-actions {
  display: flex;
  gap: 12px;
}

.refresh-btn,
.email-config-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: background 0.2s;
}

.refresh-btn {
  background: #3b82f6;
  color: white;
}

.refresh-btn:hover:not(:disabled) {
  background: #2563eb;
}

.refresh-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.email-config-btn {
  background: #64748b;
  color: white;
}

.email-config-btn:hover {
  background: #475569;
}

.email-count-badge {
  background: rgba(255, 255, 255, 0.25);
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 12px;
}

/* Stats Cards */
.stats-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
}

.stat-card {
  background: white;
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  border: 1px solid #e2e8f0;
}

.stat-label {
  font-size: 14px;
  color: #64748b;
  margin-bottom: 8px;
}

.stat-value {
  font-size: 28px;
  font-weight: 700;
  color: #1e293b;
}

/* Filters Bar */
.filters-bar {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  border: 1px solid #e2e8f0;
  margin-bottom: 24px;
  overflow: hidden;
}

.filters-header {
  padding: 16px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  user-select: none;
  background: #f8fafc;
}

.filters-header-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  color: #334155;
}

.filters-count-badge {
  background: #3b82f6;
  color: white;
  font-size: 12px;
  padding: 2px 8px;
  border-radius: 10px;
}

.collapse-icon {
  transition: transform 0.2s;
}

.collapse-icon.rotated {
  transform: rotate(-90deg);
}

.filters-content {
  padding: 20px;
}

.filter-group {
  margin-bottom: 16px;
}

.filter-group label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #475569;
  margin-bottom: 6px;
}

.filter-group input,
.filter-group select {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  font-size: 14px;
  font-family: inherit;
}

.filter-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
}

.filter-actions {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #e2e8f0;
}

.clear-filters-btn {
  padding: 8px 16px;
  background: #ef4444;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
}

.clear-filters-btn:hover {
  background: #dc2626;
}

/* Table */
.commits-table-container {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  border: 1px solid #e2e8f0;
  overflow: hidden;
}

.commits-table {
  width: 100%;
  border-collapse: collapse;
}

.commits-table thead {
  background: #f1f5f9;
  border-bottom: 2px solid #e2e8f0;
}

.commits-table th {
  padding: 12px 16px;
  text-align: right;
  font-size: 13px;
  font-weight: 600;
  color: #475569;
}

.commit-row {
  border-bottom: 1px solid #e2e8f0;
  transition: background 0.15s;
}

.commit-row:hover {
  background: #f8fafc;
}

.commit-row td {
  padding: 12px 16px;
  vertical-align: middle;
}

.expand-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #64748b;
  transition: transform 0.2s;
}

.expand-btn svg.rotated {
  transform: rotate(90deg);
}

.commit-hash {
  font-family: 'Courier New', monospace;
  background: #f1f5f9;
  padding: 4px 8px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
  color: #0ea5e9;
  font-weight: 600;
}

.commit-hash:hover {
  background: #e0e7ff;
}

.subject-cell {
  font-weight: 500;
  color: #1e293b;
}

.stats-mini {
  display: flex;
  gap: 8px;
  font-size: 12px;
  font-weight: 600;
}

.stats-added {
  color: #10b981;
}

.stats-deleted {
  color: #ef4444;
}

.stats-files {
  color: #3b82f6;
}

.email-btn {
  background: #3b82f6;
  color: white;
  border: none;
  padding: 6px 10px;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
}

.email-btn:hover:not(:disabled) {
  background: #2563eb;
}

.email-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.email-btn.sent {
  background: #10b981;
  font-weight: 600;
}

.email-btn.sent:hover:not(:disabled) {
  background: #059669;
}

/* Expanded Details */
.expanded-details td {
  background: #f8fafc;
  padding: 0;
}

.details-content {
  padding: 20px;
}

.detail-section {
  margin-bottom: 20px;
}

.detail-section:last-child {
  margin-bottom: 0;
}

.detail-section h4 {
  margin: 0 0 12px;
  font-size: 14px;
  font-weight: 600;
  color: #334155;
}

.commit-body {
  background: white;
  padding: 16px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  white-space: pre-wrap;
  font-family: 'Courier New', monospace;
  font-size: 13px;
  color: #475569;
  margin: 0;
}

.files-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.files-table th {
  background: white;
  padding: 8px 12px;
  text-align: right;
  font-weight: 600;
  color: #475569;
  border-bottom: 1px solid #e2e8f0;
}

.files-table td {
  padding: 8px 12px;
  border-bottom: 1px solid #f1f5f9;
}

.filename-cell {
  font-family: 'Courier New', monospace;
  color: #1e293b;
}

.added-cell {
  color: #10b981;
  font-weight: 600;
}

.deleted-cell {
  color: #ef4444;
  font-weight: 600;
}

.metadata {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 12px;
}

.metadata-item {
  font-size: 13px;
  color: #475569;
}

.metadata-item code {
  background: white;
  padding: 2px 6px;
  border-radius: 4px;
  font-family: 'Courier New', monospace;
  color: #0ea5e9;
}

/* Pagination */
.pagination {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-top: 1px solid #e2e8f0;
  background: #f8fafc;
}

.pagination-info {
  font-size: 14px;
  color: #64748b;
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 12px;
}

.pagination-controls button {
  padding: 8px 16px;
  background: white;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  color: #475569;
}

.pagination-controls button:hover:not(:disabled) {
  background: #f1f5f9;
}

.pagination-controls button:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.page-indicator {
  font-size: 14px;
  color: #334155;
  font-weight: 500;
}

.page-size-selector {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #475569;
}

.page-size-selector select {
  padding: 6px 10px;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  font-size: 14px;
}

/* States */
.loading-state,
.error-state,
.empty-state {
  padding: 60px 20px;
  text-align: center;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.loading-state p,
.error-state p,
.empty-state p {
  margin: 16px 0 0;
  font-size: 16px;
  color: #64748b;
}

.spinner,
.mini-spinner {
  border: 3px solid #e2e8f0;
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.spinner {
  width: 40px;
  height: 40px;
  margin: 0 auto;
}

.mini-spinner {
  width: 14px;
  height: 14px;
  display: inline-block;
  border-width: 2px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.error-state button {
  margin-top: 16px;
  padding: 10px 24px;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
}

/* Email Config Modal */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-card {
  background: white;
  border-radius: 16px;
  width: 480px;
  max-width: 90vw;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #e2e8f0;
}

.modal-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
}

.modal-close-btn {
  background: none;
  border: none;
  font-size: 24px;
  color: #94a3b8;
  cursor: pointer;
  padding: 0;
  line-height: 1;
}

.modal-close-btn:hover {
  color: #475569;
}

.modal-body {
  padding: 24px;
  overflow-y: auto;
  flex: 1;
}

.modal-empty {
  text-align: center;
  padding: 20px;
  color: #94a3b8;
  font-size: 14px;
}

.email-list {
  list-style: none;
  margin: 0 0 16px;
  padding: 0;
}

.email-list-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  margin-bottom: 8px;
  transition: all 0.2s;
}

.email-list-item.email-disabled {
  background: #f8fafc;
  border-color: #f1f5f9;
}

.email-list-item.email-disabled .email-address {
  color: #94a3b8;
  text-decoration: line-through;
}

.email-toggle-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  flex-shrink: 0;
  display: flex;
  align-items: center;
}

.toggle-track {
  display: block;
  width: 36px;
  height: 20px;
  border-radius: 10px;
  background: #cbd5e1;
  position: relative;
  transition: background 0.2s;
}

.email-toggle-btn.active .toggle-track {
  background: #10b981;
}

.toggle-thumb {
  display: block;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: white;
  position: absolute;
  top: 2px;
  right: 2px;
  transition: right 0.2s;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
}

.email-toggle-btn.active .toggle-thumb {
  right: 18px;
}

.email-address {
  font-size: 14px;
  color: #334155;
  direction: ltr;
  unicode-bidi: embed;
  flex: 1;
  transition: all 0.2s;
}

.email-remove-btn {
  background: none;
  border: none;
  color: #ef4444;
  font-size: 20px;
  cursor: pointer;
  padding: 0 4px;
  line-height: 1;
  flex-shrink: 0;
}

.email-remove-btn:hover {
  color: #dc2626;
}

.email-add-row {
  display: flex;
  gap: 8px;
}

.email-input {
  flex: 1;
  padding: 10px 12px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  font-size: 14px;
  font-family: inherit;
  direction: ltr;
}

.email-input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.email-add-btn {
  padding: 10px 20px;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  white-space: nowrap;
}

.email-add-btn:hover {
  background: #2563eb;
}

.modal-footer {
  display: flex;
  justify-content: flex-start;
  gap: 12px;
  padding: 16px 24px;
  border-top: 1px solid #e2e8f0;
}

.modal-save-btn {
  padding: 10px 24px;
  background: #10b981;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
}

.modal-save-btn:hover:not(:disabled) {
  background: #059669;
}

.modal-save-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.modal-cancel-btn {
  padding: 10px 24px;
  background: white;
  color: #475569;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
}

.modal-cancel-btn:hover {
  background: #f1f5f9;
}

/* ============================================
   Mobile-Only / Desktop-Only Utility Classes
   ============================================ */
.desktop-only {
  display: block;
}

.mobile-only {
  display: none;
}

/* ============================================
   Mobile Cards Base Styles
   ============================================ */
.commits-cards-container {
  flex-direction: column;
  gap: 10px;
}

.commit-card {
  background: white;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  overflow: hidden;
  transition: border-color 0.2s;
}

.commit-card.expanded {
  border-color: #3b82f6;
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.12);
}

.card-header {
  padding: 14px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.card-top-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-date {
  font-size: 12px;
  color: #64748b;
}

.card-subject {
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  line-height: 1.4;
  word-break: break-word;
}

.card-bottom-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 2px;
}

/* Card Expanded Details */
.card-details {
  padding: 14px;
  border-top: 1px solid #e2e8f0;
  background: #f8fafc;
  animation: slideDown 0.2s ease-out;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.card-detail-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #e2e8f0;
  font-size: 13px;
}

.card-detail-row:last-of-type {
  border-bottom: none;
}

.detail-label {
  font-weight: 600;
  color: #475569;
  flex-shrink: 0;
}

.detail-value {
  color: #334155;
  text-align: left;
  direction: ltr;
  unicode-bidi: embed;
  word-break: break-all;
  max-width: 60%;
}

.hash-copy {
  cursor: pointer;
  color: #0ea5e9;
  font-size: 11px;
}

.card-detail-section {
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px solid #e2e8f0;
}

.card-detail-section h4 {
  margin: 0 0 10px;
  font-size: 13px;
  font-weight: 600;
  color: #334155;
}

.card-files-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.card-file-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 10px;
  background: white;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
  gap: 8px;
}

.card-filename {
  font-family: 'Courier New', monospace;
  font-size: 11px;
  color: #1e293b;
  word-break: break-all;
  flex: 1;
  direction: ltr;
  unicode-bidi: embed;
}

.card-file-stats {
  display: flex;
  gap: 8px;
  font-size: 12px;
  font-weight: 600;
  flex-shrink: 0;
}

/* Mobile Pagination */
.mobile-pagination {
  margin-top: 12px;
  background: white;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  padding: 14px;
  flex-direction: column;
  gap: 10px;
  align-items: center;
}

/* ============================================
   Responsive Breakpoint: Mobile < 768px
   ============================================ */
@media (max-width: 768px) {
  /* Utility toggle */
  .desktop-only {
    display: none !important;
  }

  .mobile-only {
    display: flex !important;
  }

  /* Container */
  .commits-view {
    padding: 12px;
  }

  /* Header */
  .header {
    flex-wrap: wrap;
    gap: 10px;
    margin-bottom: 16px;
  }

  .header h1 {
    font-size: 22px;
  }

  .title-badge {
    width: 36px;
    height: 36px;
    border-radius: 10px;
  }

  .title-badge svg {
    width: 20px;
    height: 20px;
  }

  .header-actions {
    gap: 8px;
  }

  .header-actions button span {
    display: none;
  }

  .refresh-btn,
  .email-config-btn {
    padding: 10px;
    border-radius: 10px;
  }

  .email-count-badge {
    display: inline-block;
  }

  /* Stats Cards */
  .stats-cards {
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
    margin-bottom: 16px;
  }

  .stat-card {
    padding: 12px;
    border-radius: 10px;
  }

  .stat-label {
    font-size: 12px;
    margin-bottom: 4px;
  }

  .stat-value {
    font-size: 22px;
  }

  /* Filters */
  .filters-bar {
    margin-bottom: 16px;
    border-radius: 10px;
  }

  .filters-header {
    padding: 12px 14px;
  }

  .filters-content {
    padding: 14px;
  }

  .filter-row {
    grid-template-columns: 1fr;
    gap: 0;
  }

  .filter-group input,
  .filter-group select {
    padding: 10px;
    font-size: 14px;
    border-radius: 8px;
  }

  /* Pagination (desktop one is hidden, but just in case) */
  .pagination {
    flex-direction: column;
    gap: 10px;
    align-items: center;
    padding: 12px;
  }

  .page-size-selector {
    display: none;
  }

  /* Modal */
  .modal-card {
    width: 95vw;
    max-width: 95vw;
    border-radius: 14px;
    margin: 0 auto;
  }

  .modal-header {
    padding: 16px 18px;
  }

  .modal-header h3 {
    font-size: 16px;
  }

  .modal-body {
    padding: 16px 18px;
  }

  .modal-footer {
    padding: 14px 18px;
  }

  /* Loading / Error / Empty states */
  .loading-state,
  .error-state,
  .empty-state {
    padding: 40px 16px;
    border-radius: 10px;
  }
}
</style>
